import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw } from "lucide-react";
import { formatEventTime } from "./event-utils";
import type { OutgoingMessageAttempt } from "./message-utils";

type AgentOutgoingMessageProps = {
  attempt: OutgoingMessageAttempt;
  retryDisabled: boolean;
  onRetry: (attempt: OutgoingMessageAttempt) => void;
};

export function AgentOutgoingMessage({
  attempt,
  retryDisabled,
  onRetry,
}: AgentOutgoingMessageProps) {
  if (attempt.requestKind === "workflow_start") {
    if (attempt.status === "pending") {
      return null;
    }
    return (
      <article
        aria-label="Lead research failed to start"
        className="flex min-w-0 justify-center"
      >
        <div className="min-w-0 max-w-xl rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-900 text-sm">
          <p className="font-medium">Lead research failed to start</p>
          <p className="mt-1 text-xs">
            {attempt.errorMessage ??
              "The workflow could not be started. Please try again."}
          </p>
          {attempt.retryable ? (
            <Button
              className="mt-3"
              disabled={retryDisabled}
              onClick={() => onRetry(attempt)}
              size="sm"
              type="button"
              variant="outline"
            >
              <RotateCcw aria-hidden="true" />
              Retry
            </Button>
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <article
      aria-label={`Your message is ${attempt.status}`}
      className="flex min-w-0 justify-end"
    >
      <div className="min-w-0 max-w-[88%] rounded-2xl rounded-br-md bg-brand-darkGreen px-4 py-3 text-sm text-white shadow-xs md:max-w-[75%]">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs opacity-75">
          <span className="font-medium">You</span>
          <time dateTime={attempt.submittedAt}>
            {formatEventTime(attempt.submittedAt)}
          </time>
        </div>

        <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
          {attempt.text}
        </p>

        {attempt.status === "pending" ? (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <Loader2 aria-hidden="true" className="size-3 animate-spin" />
            Waiting for the agent
          </div>
        ) : (
          <div className="mt-3 border-white/20 border-t pt-3">
            <p className="text-xs">
              {attempt.errorMessage ??
                "The message could not be sent. Please try again."}
            </p>
            {attempt.retryable ? (
              <Button
                className="mt-2 bg-white text-brand-darkGreen hover:bg-white/90"
                disabled={retryDisabled}
                onClick={() => onRetry(attempt)}
                size="sm"
                type="button"
              >
                <RotateCcw aria-hidden="true" />
                Retry
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
}
